// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { Modifier, RichUtils, EditorState } from "draft-js";
import createToolbarPlugin from "draft-js-static-toolbar-plugin";
import { Toastr } from "@revvsales/components";

// Components Imports
import RevvToolBar from "../RevvToolBar";
import HyperlinkBox from "../DocumentItems/HyperlinkBox";
import applyFontStyle from "./applyFontStyle";

// Actions Imports
import { EditorActions, NotesActions } from "../../actions";
import {
  changeToolsView,
  preventEditorToDeactivate,
  handleCustomColorDropDownOpen,
  updateDocMeta,
} from "../../actions/DocumentActions/index";

import { updateTemplateMeta } from "../../actions/DocumentTemplateActions/templateActions";

// Internal Imports
import {
  setBlockType,
  setBlockData,
  toggleBlockIndent,
  updateSelectionForEntity,
  removeStyleFromCurrentBlock,
  getSelectedBlockTypes,
  addInlineStyleToCurrentBlock,
  getClosestFontWeightStyle,
} from "../../utils/draftUtils";
import { emptyNote } from "../Notes/utils";
import { DOCUMENTS } from "../../constants";
import { findClosestSize } from "../../constants/fontSizes";
import "draft-js-static-toolbar-plugin/lib/plugin.css";

// Destructuring
const { FONT_SIZE_OPTIONS, HEADER_FONT_SIZES } = DOCUMENTS;
const {
  nukeEditorState,
  updateEditorState,
  updateEditorHighlightSelection,
} = EditorActions;
const { addNote } = NotesActions;
const staticToolbarPlugin = createToolbarPlugin();
const { Toolbar } = staticToolbarPlugin;

/**
 * :::===::: CustomRevvToolbar :::===:::
 * This component will be used for two scenarios,
 * 1. When both the Text Editor and the Toolbar come separatly(ex: Custom Text and Custom Table Text),
 * 2. When both the Text Editor and the Toolbar come together(ex: Add Clause Option and Form mode doc text area)
 */
class CustomRevvToolbar extends Component {
  state = {
    showHyperlinkBoxConfirmation: false,
    tempEditorState: null,
    tempCurrentSection: null,
  };

  _getEditorState = () => {
    const { isSeparateToolbar, localEditorState } = this.props;
    if (isSeparateToolbar) {
      return this.props.clickedEditorState;
    }
    return localEditorState;
  };

  _onChange = editorState => {
    if (this.props.isSeparateToolbar) {
      this.props.updateEditorState(editorState);
    } else {
      this.props.updateLocalEditorState(editorState);
    }
  };

  _handleChange = newEditorState => {
    this._onChange(newEditorState);
  };

  _setBlockType = blockType => {
    const editorState = this._getEditorState();
    let newEditorState = editorState;
    const currentFocus = editorState.getSelection().getFocusKey();
    const inlineStyle = editorState.getCurrentInlineStyle(currentFocus);
    let fontSizeName;

    if (blockType === "unstyled") {
      // Default Font size for unstyled block
      fontSizeName = "FONTSIZE_FOURTEEN";
    }

    // in case of header remove font size & apply correct one
    if (
      blockType === "header-one" ||
      blockType === "header-two" ||
      blockType === "header-three" ||
      blockType === "header-four"
    ) {
      const fontSize = findClosestSize(HEADER_FONT_SIZES[blockType]);
      newEditorState = addInlineStyleToCurrentBlock(
        editorState,
        fontSize.name,
        ["FONTSIZE", "BOLD"],
      );
      fontSizeName = fontSize.name;
    }

    if (inlineStyle.size > 0) {
      if (
        blockType === "header-one" ||
        blockType === "header-two" ||
        blockType === "header-three" ||
        blockType === "header-four"
      ) {
        newEditorState = removeStyleFromCurrentBlock(
          newEditorState,
          "FONTWEIGHT",
        );
        newEditorState = setBlockType(blockType, newEditorState);
      } else {
        const nextContentState = Object.keys(FONT_SIZE_OPTIONS).reduce(
          (contentState, size) =>
            Modifier.removeInlineStyle(
              contentState,
              editorState.getSelection(),
              size,
            ),
          editorState.getCurrentContent(),
        );

        const nextEditorState = EditorState.push(
          editorState,
          nextContentState,
          "change-inline-style",
        );
        newEditorState = setBlockType(blockType, nextEditorState);
      }
    } else {
      newEditorState = setBlockType(blockType, newEditorState);
    }
    const metadata = [
      {
        op: "replace",
        field: "meta_data",
        value: {
          meta_data: {
            ...this.props.metaData,
            FONTSIZE: fontSizeName,
            BLOCKTYPE: blockType,
          },
        },
      },
    ];
    this._saveMetaData(metadata);
    this._handleChange(newEditorState);
  };

  _setBlockTypeNormal = () => {
    this._setBlockType("unstyled");
  };

  _handleHeaderButton = buttonType => {
    this._setBlockType(buttonType);
  };

  _handleAlignment = type => {
    const editorState = this._getEditorState();
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    let updatedContentState = contentState;

    // check if there is any selection or not
    if (selectionState.isCollapsed()) {
      // if there is no selection, just toggle alignment
      const blockKey = selectionState.getAnchorKey();

      // check if block type is list: disable if so
      const blockType = contentState.getBlockForKey(blockKey).getType();
      if (blockType.includes("list")) {
        return;
      }

      updatedContentState = this._toggleBlockAlignment(
        type,
        updatedContentState,
        blockKey,
      );
    } else {
      // if there is any selection, update the alignment
      // for each block under selection
      // get the start key
      let startKey = selectionState.getStartKey();
      // and update till endKey
      const endKey = contentState.getKeyAfter(selectionState.getEndKey());
      while (startKey !== endKey) {
        updatedContentState = this._toggleBlockAlignment(
          type,
          updatedContentState,
          startKey,
        );
        startKey = contentState.getKeyAfter(startKey);
      }
    }

    // set the new editor state
    const updatedEditorState = EditorState.set(editorState, {
      currentContent: updatedContentState,
    });
    this._handleChange(updatedEditorState);
  };

  _toggleBlockAlignment = (type, contentState, blockKey) => {
    // get the block
    const block = contentState.getBlockForKey(blockKey);
    // get block data
    let blockData = block.data;
    // get element style of the block, if any
    const elementStyle = blockData.get("elementStyle");
    // get all the styles
    const styles =
      typeof elementStyle === "string" ? elementStyle.split(";") : [];

    // check if text-align is there or not
    // if there is any, find the index
    const textAlignIndex = styles.findIndex(style =>
      style.startsWith("text-align"),
    );

    if (textAlignIndex > -1) {
      // if text-align is present, update that index
      styles[textAlignIndex] = `text-align:${type}`;
    } else {
      // push text-align
      styles.push(`text-align:${type}`);
    }

    // update block data
    blockData = blockData.set("elementStyle", styles.join(";"));

    // set new block data to the block
    const updatedContentState = setBlockData(blockData, contentState, blockKey);
    return updatedContentState;
  };

  _handleIndent = type => {
    const editorState = this._getEditorState();
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    let updatedContentState = contentState;

    // check if there is any selection or not
    if (selectionState.isCollapsed()) {
      // if there is no selection, just toggle alignment
      const blockKey = selectionState.getAnchorKey();
      updatedContentState = toggleBlockIndent(
        type,
        updatedContentState,
        blockKey,
      );
    } else {
      // if there is any selection, update the alignment
      // for each block under selection
      // get the start key
      let startKey = selectionState.getStartKey();
      // and update till endKey
      const endKey = contentState.getKeyAfter(selectionState.getEndKey());
      while (startKey !== endKey) {
        updatedContentState = toggleBlockIndent(
          type,
          updatedContentState,
          startKey,
        );
        startKey = contentState.getKeyAfter(startKey);
      }
    }

    // set the new editor state
    const updatedEditorState = EditorState.set(editorState, {
      currentContent: updatedContentState,
    });
    this._handleChange(updatedEditorState);
  };

  _handleListButton = type => {
    const editorState = this._getEditorState();
    const updatedEditorState = setBlockType(type, editorState);
    this._handleChange(updatedEditorState);
  };

  _highlightSelection = () => {
    const editorState = this._getEditorState();
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    const contentStateWithEntity = currentContent.createEntity(
      "HIGHLIGHT",
      "MUTABLE",
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const updatedContent = Modifier.applyEntity(
      currentContent,
      selection,
      entityKey,
    );
    this.props.updateEditorHighlightSelection(updatedContent);
  };

  _fontFamilyUpdate = (fontStyle, fontValue, fontWeightStyle) => {
    const editorState = this._getEditorState();
    let newEditorState = editorState;
    if (!fontWeightStyle) {
      // if no font weight, get current font weight & find closed one
      const currentInlineStyles = editorState.getCurrentInlineStyle();
      if (currentInlineStyles && currentInlineStyles.size > 0) {
        const oldFontWeight = currentInlineStyles.filter(style =>
          style.startsWith("FONTWEIGHT"),
        );
        if (oldFontWeight && oldFontWeight.size > 0) {
          const { newFontWeightStyle } = getClosestFontWeightStyle(
            [...oldFontWeight][0],
            fontStyle,
            fontValue,
          );
          fontWeightStyle = newFontWeightStyle;
        }
      }
    }

    // ADD NEW
    newEditorState = applyFontStyle(newEditorState, fontStyle, "FONTFAMILY");
    if (fontWeightStyle) {
      // remove bold
      const nextContentState = Modifier.removeInlineStyle(
        newEditorState.getCurrentContent(),
        newEditorState.getSelection(),
        "BOLD",
      );
      newEditorState = EditorState.push(
        newEditorState,
        nextContentState,
        "change-inline-style",
      );

      newEditorState = applyFontStyle(
        newEditorState,
        fontWeightStyle,
        "FONTWEIGHT",
      );
    }

    this._handleChange(newEditorState);
  };

  _handleBIUButtonClick = (buttonType, data) => {
    let editorState = this._getEditorState();
    /**
     * ####### Update selection for "DYNAMICFIELD" #######
     * here, need to update the selection state
     * if the anchor or focus or both positions have "DYNAMICFIELD",
     * so that if apply styles on one word of DF, it must apply on all words of DF
     */
    const updatedSelectionForEntity = updateSelectionForEntity(
      editorState,
      "DYNAMICFIELD",
    );
    if (updatedSelectionForEntity) {
      const newEditorState = EditorState.acceptSelection(
        editorState,
        updatedSelectionForEntity,
      );
      editorState = newEditorState;
    }
    if (
      buttonType === "FONTSIZE" ||
      buttonType === "FONTCOLOR" ||
      buttonType === "FONTFAMILY" ||
      buttonType === "FONTWEIGHT"
    ) {
      let nextEditorState = editorState;
      // Checking if there is "BOLD" applied, then
      // before applying "FONTWEIGHT", removing the "BOLD"
      if (buttonType === "FONTWEIGHT") {
        const nextContentState = Modifier.removeInlineStyle(
          editorState.getCurrentContent(),
          editorState.getSelection(),
          "BOLD",
        );
        nextEditorState = EditorState.push(
          editorState,
          nextContentState,
          "change-inline-style",
        );
      }

      if (editorState.getSelection().isCollapsed()) {
        nextEditorState = applyFontStyle(nextEditorState, data, buttonType);
      } else {
        nextEditorState = applyFontStyle(nextEditorState, data, buttonType);
      }
      this._handleChange(nextEditorState);
    } else {
      let shouldToggleBold = true;

      // if removing BOLD and block type is header then remove header
      const blockTypes = getSelectedBlockTypes(editorState);
      const headerType =
        blockTypes && blockTypes.length > 0
          ? blockTypes.find(type => type && type.includes("header"))
          : null;
      if (buttonType === "BOLD" && headerType) {
        editorState = setBlockType("unstyled", editorState);
        shouldToggleBold = false;
      }

      // Checking if there is "font-family" attached with corresponding "font-weight", then
      // before applying "BOLD", removing the "font-weight", otherwise "BOLD" will not work
      else if (buttonType === "BOLD") {
        editorState = removeStyleFromCurrentBlock(editorState, "FONTWEIGHT");
      }

      if (shouldToggleBold) {
        const updateES = RichUtils.toggleInlineStyle(editorState, buttonType);
        this._handleChange(updateES);

        const metadata = [
          {
            op: "replace",
            field: "meta_data",
            value: {
              meta_data: {
                ...this.props.metaData,
                [buttonType]: updateES.getCurrentInlineStyle().has(buttonType),
              },
            },
          },
        ];

        setTimeout(() => {
          this._saveMetaData(metadata);
        }, 10);

        this._handleChange(updateES);
      } else {
        if (
          buttonType === "BOLD" &&
          !editorState.getCurrentInlineStyle().has(buttonType) &&
          headerType.startsWith("header")
        ) {
          const metadata = [
            {
              op: "replace",
              field: "meta_data",
              value: {
                meta_data: {
                  ...this.props.metaData,
                  [buttonType]: editorState
                    .getCurrentInlineStyle()
                    .has(buttonType),
                  BLOCKTYPE: "unstyled",
                  // FONTSIZE: "FONTSIZE_FOURTEEN",
                },
              },
            },
          ];
          setTimeout(() => {
            this._saveMetaData(metadata);
          }, 10);
        }
        this._handleChange(editorState);
      }
    }
  };

  _saveMetaData = data => {
    if (this.props.mode === "DOCUMENT") {
      this.props
        .updateDocMeta({ docId: this.props.docID, data })
        .then()
        .catch(() => {
          Toastr.showToastr({
            type: "failure",
            message: "Oops, Something went wrong!!",
            timeout: 3000,
          });
        });
    }
    if (this.props.mode === "TEMPLATE") {
      this.props
        .updateTemplateMeta({ docId: this.props.docID, data })
        .then()
        .catch(() => {
          Toastr.showToastr({
            type: "failure",
            message: "Oops, Something went wrong!!",
            timeout: 3000,
          });
        });
    }
  };

  _showHyperlinkBox = (tempEditorState, tempCurrentSection) => {
    this.setState({
      showHyperlinkBoxConfirmation: true,
      tempEditorState,
      tempCurrentSection,
    });
  };

  _closeHyperlinkBox = () => {
    this.setState({
      showHyperlinkBoxConfirmation: false,
      tempEditorState: null,
      tempCurrentSection: null,
    });
  };

  _addNote = (text, editorState, component, removeHighlightSelection) => {
    const { docID, updateSection } = this.props;
    const note = emptyNote(docID, null);
    note.content = {
      highlight: text,
      message: "",
    };
    note.anchor = {
      component: "DOCUMENT",
      action: "HIGHLIGHT",
    };
    note.component = component;
    note.editorState = editorState;
    note.updateSection = updateSection;
    note.removeHighlightSelection = removeHighlightSelection;
    note.activeComponentMeta = this.props.activeComponentMeta;
    this.props.addNote(note);
    this.props.changeToolsView("notes");
  };

  _getComponent = () => {
    const { isSeparateToolbar, clickedSection, section } = this.props;
    if (isSeparateToolbar) {
      return clickedSection;
    }
    return section;
  };

  render() {
    const {
      showHyperlinkBoxConfirmation,
      tempCurrentSection,
      tempEditorState,
    } = this.state;
    const {
      mode,
      updateSection,
      removeHighlightSelection,
      handleUndo,
      handleRedo,
      isSeparateToolbar,
      metaData,
    } = this.props;
    let hyperlinkPopup = null;

    const editorState = this._getEditorState();
    const component = this._getComponent();

    if (showHyperlinkBoxConfirmation) {
      hyperlinkPopup = (
        <HyperlinkBox
          mode={mode}
          section={component || tempCurrentSection}
          editorState={editorState || tempEditorState}
          updateSection={updateSection}
          show={showHyperlinkBoxConfirmation}
          closeHyperlinkBox={this._closeHyperlinkBox}
        />
      );
    }

    return (
      <div
        className={`${isSeparateToolbar ? "customrevvtoolbar-container" : ""}`}
        ref={this.props.customToolbarRef}>
        {hyperlinkPopup}
        <Toolbar>
          {externalProps => (
            <RevvToolBar
              mode={mode}
              externalProps={externalProps}
              component={component}
              addNote={this._addNote}
              editorState={editorState}
              handleUndo={handleUndo}
              handleRedo={handleRedo}
              handleBIUButtonClick={this._handleBIUButtonClick}
              fontFamilyUpdate={this._fontFamilyUpdate}
              handleListButton={this._handleListButton}
              handleHeaderButton={this._handleHeaderButton}
              handleNormalText={this._setBlockTypeNormal}
              handleAlignment={this._handleAlignment}
              handleIndent={this._handleIndent}
              highlightSelection={this._highlightSelection}
              removeHighlightSelection={removeHighlightSelection}
              getEditorState={this._getEditorStateForHyperlink}
              showHyperlinkBox={this._showHyperlinkBox}
              closeHyperlinkBox={this._closeHyperlinkBox}
              isSeparateToolbar={isSeparateToolbar}
              preventEditorToDeactivate={this.props.preventEditorToDeactivate}
              handleCustomColorDropDownOpen={
                this.props.handleCustomColorDropDownOpen
              }
              toolbarCustomColorDropdownRef={
                this.props.toolbarCustomColorDropdownRef
              }
              metaData={metaData}
              saveMetaData={this._saveMetaData}
            />
          )}
        </Toolbar>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const {
    clickedEditorState,
    clickedSection,
    highlightedContent,
  } = state.EditorStore;
  const {
    activeComponentMeta,
    document: { meta_data: docMetaData },
  } = state.DocumentStore;

  const {
    template: { meta_data: tempMetaData },
  } = state.TemplateStore;

  let metaData;

  if (props.mode === "DOCUMENT") {
    metaData = docMetaData;
  }

  if (props.mode === "TEMPLATE") {
    metaData = tempMetaData;
  }
  return {
    clickedSection,
    clickedEditorState,
    highlightedContent,
    activeComponentMeta,
    metaData,
  };
};

const mapDispatchToProps = {
  addNote,
  changeToolsView,
  nukeEditorState,
  updateEditorState,
  preventEditorToDeactivate,
  handleCustomColorDropDownOpen,
  updateEditorHighlightSelection,
  updateDocMeta,
  updateTemplateMeta,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CustomRevvToolbar);



// WEBPACK FOOTER //
// ./src/components/CustomRevvToolbar/index.js